import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories, featuredComics, latestUpdates, popularRanking } from '@/mocks/home';
import type { ComicItem } from '@/mocks/home';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs w-3 h-3 flex items-center justify-center ${
            star <= Math.round(rating / 2)
              ? 'ri-star-fill text-primary-500'
              : 'ri-star-line text-foreground-300'
          }`}
        ></i>
      ))}
      <span className="text-xs font-semibold text-primary-600 ml-1">{rating}</span>
    </div>
  );
}

function ComicCard({ comic }: { comic: ComicItem }) {
  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩漫在线观看`}
          title={`${comic.title} - 韩漫在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
            {comic.rating}
          </span>
        </div>
        {comic.status === '连载中' && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
              连载
            </span>
          </div>
        )}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
        {comic.title}
      </h3>
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-xs text-foreground-500">{comic.author}</span>
        <StarRating rating={comic.rating} />
      </div>
      <p className="mt-1 text-xs text-foreground-400 line-clamp-1">{comic.latestChapter}</p>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);

  const allComics = useMemo(() => {
    const all: ComicItem[] = [];
    const seen = new Set<string>();
    for (const comic of [...featuredComics, ...latestUpdates, ...popularRanking]) {
      if (!seen.has(comic.id)) {
        seen.add(comic.id);
        all.push(comic);
      }
    }
    return all;
  }, []);

  const filteredComics = useMemo(() => {
    if (!slug) return allComics;
    return allComics.filter((c) => c.category === slug);
  }, [slug, allComics]);

  const webpageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'}漫画 - 韩漫网`,
    description: currentCategory?.description || '韩漫分类浏览',
    url: `https://thingschat.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://thingschat.cn',
    },
  }), [slug, currentCategory]);

  const iconMap: Record<string, string> = {
    'ri-heart-line': 'ri-heart-line',
    'ri-magic-line': 'ri-magic-line',
    'ri-sword-line': 'ri-sword-line',
    'ri-search-eye-line': 'ri-search-eye-line',
    'ri-emotion-laugh-line': 'ri-emotion-laugh-line',
    'ri-film-line': 'ri-film-line',
    'ri-ghost-line': 'ri-ghost-line',
    'ri-building-line': 'ri-building-line',
    'ri-briefcase-line': 'ri-briefcase-line',
    'ri-ancient-gate-line': 'ri-ancient-gate-line',
    'ri-basketball-line': 'ri-basketball-line',
    'ri-loop-left-line': 'ri-loop-left-line',
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebPage" data={webpageSchema} />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {currentCategory ? `${currentCategory.name}漫画` : '全部分类'}
            </h1>
            {currentCategory && (
              <p className="mt-2 text-sm text-foreground-500">{currentCategory.description}</p>
            )}
          </div>

          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Link
              to="/category/romance"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                !slug || slug === 'romance'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="mt-8">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-bookmark-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画内容</p>
                <Link
                  to="/category/romance"
                  className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 font-medium hover:text-primary-700 whitespace-nowrap"
                >
                  浏览其他分类 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}